"use client";

import { useState } from "react";

interface Props {
  specs: Record<string, string>;
  /** При количестве строк > collapseThreshold показывается «Показать все».
   *  По умолчанию 12. */
  collapseThreshold?: number;
}

interface PackagingRow {
  pkg: string;
  size: string;
  weight: string;
}

interface SplitResult {
  general: Array<[string, string]>;
  totalWeight: string | null;
  packaging: PackagingRow[];
}

/**
 * Парсер парсит блок «**Габариты и вес товара с упаковкой» в плоские ключи
 * `Упаковка №N — размеры/вес` + `Общий вес с упаковкой`. Здесь мы вытаскиваем
 * их обратно из общего словаря specs, чтобы отрисовать как отдельный блок,
 * как на доноре.
 */
function splitPackagingSpecs(specs: Record<string, string>): SplitResult {
  const general: Array<[string, string]> = [];
  const pkgMap = new Map<string, PackagingRow>();
  let totalWeight: string | null = null;
  const reSize = /^(Упаковка\s+№\d+)\s*—\s*размеры/i;
  const reWeight = /^(Упаковка\s+№\d+)\s*—\s*вес/i;

  for (const [k, v] of Object.entries(specs)) {
    if (k === "Общий вес с упаковкой") {
      totalWeight = v;
      continue;
    }
    const ms = k.match(reSize);
    if (ms) {
      const pkg = ms[1];
      const row = pkgMap.get(pkg) ?? { pkg, size: "", weight: "" };
      row.size = v;
      pkgMap.set(pkg, row);
      continue;
    }
    const mw = k.match(reWeight);
    if (mw) {
      const pkg = mw[1];
      const row = pkgMap.get(pkg) ?? { pkg, size: "", weight: "" };
      row.weight = v;
      pkgMap.set(pkg, row);
      continue;
    }
    general.push([k, v]);
  }

  const packaging = Array.from(pkgMap.values()).sort((a, b) =>
    a.pkg.localeCompare(b.pkg, "ru", { numeric: true }),
  );
  return { general, totalWeight, packaging };
}

/**
 * Технические характеристики товара.
 * - Зебра-полосы для длинных списков.
 * - Hover-подсветка строки.
 * - «Показать все N характеристик» при превышении порога.
 * - Отдельный свёрнутый блок «Габариты и вес товара с упаковкой» внизу.
 *
 * Для SEO все строки в DOM, после порога скрыты через hidden (а не условный рендер).
 */
export function ProductSpecsTable({ specs, collapseThreshold = 12 }: Props) {
  const { general, totalWeight, packaging } = splitPackagingSpecs(specs);
  const total = general.length;
  const [expanded, setExpanded] = useState(false);
  const [pkgExpanded, setPkgExpanded] = useState(false);
  const hasCollapse = total > collapseThreshold;
  const hasPackaging = totalWeight !== null || packaging.length > 0;

  return (
    <div>
      <dl
        aria-label="Технические характеристики"
        className="overflow-hidden rounded-[12px] border border-border bg-white"
      >
        {general.map(([k, v], idx) => {
          const hidden = hasCollapse && !expanded && idx >= collapseThreshold;
          return (
            <div
              key={k}
              hidden={hidden}
              className={`grid grid-cols-[minmax(0,11rem)_1fr] gap-4 px-4 py-2.5 transition-colors hover:bg-brand-100 ${
                idx % 2 === 1 ? "bg-bg-muted" : ""
              }`}
            >
              <dt className="text-sm text-text-muted">{k}</dt>
              <dd className="text-sm font-medium text-text">{v}</dd>
            </div>
          );
        })}
      </dl>
      {hasCollapse && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-3 inline-flex items-center gap-1 rounded-[8px] border border-border bg-white px-4 py-2 text-sm font-medium text-text transition-colors hover:border-brand-500 hover:bg-bg-muted"
        >
          {expanded ? "Свернуть" : `Показать все ${total} характеристик`}
          <svg
            aria-hidden
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            style={{
              transform: expanded ? "rotate(180deg)" : "none",
              transition: "transform 0.2s",
            }}
          >
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {hasPackaging && (
        <section className="mt-6" aria-label="Габариты и вес товара с упаковкой">
          <h3 className="mb-3 text-sm font-semibold text-text">
            **Габариты и вес товара с упаковкой
          </h3>
          <button
            type="button"
            onClick={() => setPkgExpanded((v) => !v)}
            aria-expanded={pkgExpanded}
            disabled={packaging.length === 0}
            className="flex w-full items-center justify-center gap-2 rounded-[12px] border border-border bg-bg-muted px-4 py-3 text-sm font-medium text-text transition-colors hover:bg-brand-100 disabled:cursor-default disabled:hover:bg-bg-muted"
          >
            <span>
              {totalWeight ? `Общий вес с упаковкой — ${totalWeight}` : "Габариты упаковок"}
            </span>
            {packaging.length > 0 && (
              <svg
                aria-hidden
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                style={{
                  transform: pkgExpanded ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              >
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
          {packaging.length > 0 && pkgExpanded && (
            <div className="mt-3 overflow-hidden rounded-[12px] border border-border bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-bg-muted text-text-muted">
                    <th className="px-4 py-2 text-left font-medium">Упаковка</th>
                    <th className="px-4 py-2 text-left font-medium">
                      Размеры (Д×Ш×В), мм
                    </th>
                    <th className="px-4 py-2 text-left font-medium">Вес, кг</th>
                  </tr>
                </thead>
                <tbody>
                  {packaging.map((row, idx) => (
                    <tr
                      key={row.pkg}
                      className={idx % 2 === 1 ? "bg-bg-muted" : ""}
                    >
                      <td className="px-4 py-2.5 font-medium text-text">{row.pkg}</td>
                      <td className="px-4 py-2.5 text-text tabular">{row.size || "—"}</td>
                      <td className="px-4 py-2.5 text-text tabular">
                        {row.weight || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
